import { useState, useCallback } from "react";
import type { ApiClientInterface } from "../infrastructure/api-client";
import type {
  AutocompleteSuggestion,
  PlaceDetails,
  PlaceResult,
  UsePlaceAutocompleteOptions,
  UsePlaceAutocompleteReturn,
} from "./types";

/**
 * Hook for place autocomplete functionality
 * Abstracts API calls from presentation layer
 */
export function usePlaceAutocomplete(
  apiClient: ApiClientInterface,
  options: UsePlaceAutocompleteOptions = {}
): UsePlaceAutocompleteReturn {
  const { minInputLength = 2 } = options;

  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (input.length < minInputLength) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await apiClient.get<AutocompleteSuggestion[]>(
          `/api/places/autocomplete?input=${encodeURIComponent(input)}`
        );
        if (response.success && response.data) {
          setSuggestions(response.data);
        } else {
          setSuggestions([]);
        }
      } catch {
        setSuggestions([]);
      }
      setIsLoading(false);
    },
    [apiClient, minInputLength]
  );

  const selectSuggestion = useCallback(
    async (suggestion: AutocompleteSuggestion): Promise<PlaceResult | null> => {
      setIsFetchingDetails(true);
      try {
        const response = await apiClient.get<PlaceDetails>(
          `/api/places/${encodeURIComponent(suggestion.placeId)}`
        );

        if (response.success && response.data) {
          const result: PlaceResult = {
            name: response.data.name,
            placeId: response.data.placeId,
            latitude: response.data.latitude,
            longitude: response.data.longitude,
            formattedAddress: response.data.formattedAddress,
          };
          setSuggestions([]);
          return result;
        }
        return null;
      } catch {
        return null;
      } finally {
        setIsFetchingDetails(false);
      }
    },
    [apiClient]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return {
    suggestions,
    isLoading,
    isFetchingDetails,
    fetchSuggestions,
    selectSuggestion,
    clearSuggestions,
  };
}
